import { supabase } from '../lib/supabase';

export const discussionService = {
  // Get all active topics with author info and comment count
  async getTopics() {
    const { data, error } = await supabase
      ?.from('discussion_topics')
      ?.select(`
        *,
        author:user_profiles!discussion_topics_user_id_fkey(id, full_name, email),
        discussion_comments(count)
      `)
      ?.eq('is_active', true)
      ?.order('created_at', { ascending: false });

    if (error) throw error;

    return (data || [])?.map((topic) => ({
      id: topic?.id,
      userId: topic?.user_id,
      title: topic?.title,
      body: topic?.body,
      createdAt: topic?.created_at,
      isActive: topic?.is_active,
      authorName: topic?.author?.full_name || topic?.author?.email || 'Korisnik',
      commentCount: topic?.discussion_comments?.[0]?.count || 0,
    }));
  },

  // Get single topic with comments
  async getTopicWithComments(topicId) {
    const { data: topic, error: topicError } = await supabase
      ?.from('discussion_topics')
      ?.select(`
        *,
        author:user_profiles!discussion_topics_user_id_fkey(id, full_name, email)
      `)
      ?.eq('id', topicId)
      ?.eq('is_active', true)
      ?.single();

    if (topicError) throw topicError;

    const { data: comments, error: commentsError } = await supabase
      ?.from('discussion_comments')
      ?.select(`
        *,
        author:user_profiles!discussion_comments_user_id_fkey(id, full_name, email)
      `)
      ?.eq('topic_id', topicId)
      ?.eq('is_active', true)
      ?.order('created_at', { ascending: true });

    if (commentsError) throw commentsError;

    return {
      id: topic?.id,
      userId: topic?.user_id,
      title: topic?.title,
      body: topic?.body,
      createdAt: topic?.created_at,
      authorName: topic?.author?.full_name || topic?.author?.email || 'Korisnik',
      comments: (comments || [])?.map((c) => ({
        id: c?.id,
        topicId: c?.topic_id,
        userId: c?.user_id,
        body: c?.body,
        createdAt: c?.created_at,
        authorName: c?.author?.full_name || c?.author?.email || 'Korisnik',
      })),
    };
  },

  // Create a new topic
  async createTopic({ title, body }) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Niste prijavljeni');

    const { data, error } = await supabase
      ?.from('discussion_topics')
      ?.insert({ user_id: user?.id, title, body })
      ?.select(`
        *,
        author:user_profiles!discussion_topics_user_id_fkey(id, full_name, email)
      `)
      ?.single();

    if (error) throw error;

    return {
      id: data?.id,
      userId: data?.user_id,
      title: data?.title,
      body: data?.body,
      createdAt: data?.created_at,
      authorName: data?.author?.full_name || data?.author?.email || 'Korisnik',
      commentCount: 0,
    };
  },

  // Add a comment to a topic
  async addComment({ topicId, body }) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Niste prijavljeni');

    const { data, error } = await supabase
      ?.from('discussion_comments')
      ?.insert({ topic_id: topicId, user_id: user?.id, body })
      ?.select(`
        *,
        author:user_profiles!discussion_comments_user_id_fkey(id, full_name, email)
      `)
      ?.single();

    if (error) throw error;

    return {
      id: data?.id,
      topicId: data?.topic_id,
      userId: data?.user_id,
      body: data?.body,
      createdAt: data?.created_at,
      authorName: data?.author?.full_name || data?.author?.email || 'Korisnik',
    };
  },

  // Soft-delete a topic (admin or owner)
  async deleteTopic(topicId) {
    const { error } = await supabase
      ?.from('discussion_topics')
      ?.update({ is_active: false })
      ?.eq('id', topicId);

    if (error) throw error;
  },

  // Soft-delete a comment (admin or owner)
  async deleteComment(commentId) {
    const { error } = await supabase
      ?.from('discussion_comments')
      ?.update({ is_active: false })
      ?.eq('id', commentId);

    if (error) throw error;
  },
};
